import { isbot } from 'isbot';
import type { TrackJSErrorPayload, TrackJSInstallOptions } from 'trackjs';
import { TrackJS } from 'trackjs';

interface InitTrackJsArgs extends Omit<
  TrackJSInstallOptions,
  'application' | 'forwardingDomain' | 'token' | 'version'
> {
  application: string;
  forwardingDomain: string;
  token: string;
  version: string;
}

export const initTrackJS = ({
  application,
  forwardingDomain,
  token,
  version,
  ...rest
}: InitTrackJsArgs) => {
  TrackJS.install({
    application,
    forwardingDomain,
    onError: defaultOnError,
    token,
    version,
    ...rest,
  });
};

const redactCreditCardNumber = (value: string) => {
  if (typeof value === 'string') {
    return value.replace(
      /\b\d{16}\b/gi,
      'REDACTED_POTENTIAL_CREDIT_CARD_NUMBER'
    );
  }

  return value;
};

// TrackJS agent is built into its own `trackjs-<hash>.js` chunk (see
// `vite-plugins/code-splitting-groups.ts`) so its frames can be told apart
// from the rest of our code.
const TRACKJS_FRAME = /iheartjane[^\n]*\/trackjs-[\w-]+\.js/;

/**
 * Verify error stack trace includes our code (`iheartjane`), excluding TrackJS's own instrumentation frames.
 */
export const shouldReportError = (stack?: string) => {
  if (!stack) return false;

  return stack
    .split('\n')
    .filter((line) => !TRACKJS_FRAME.test(line))
    .some((line) => /iheartjane/.test(line));
};

const defaultOnError = (payload: TrackJSErrorPayload) => {
  if (isbot(navigator.userAgent) || !shouldReportError(payload.stack)) {
    return false;
  }
  payload.message = redactCreditCardNumber(payload.message);
  payload.stack = redactCreditCardNumber(payload.stack);
  return true;
};

export const registerErrorMetadata = (key?: string, val?: string) => {
  if (key && val) {
    TrackJS.addMetadata(key, val);
  }
};

export const identify = (userId: string) => {
  TrackJS.configure({ userId });
};

export const debugLog = (log: string) => {
  if (TrackJS.isInstalled()) {
    TrackJS.console.debug(log);
  }
};
